import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { PaymentPlanAuditService } from '../../services/payment-plan-audit.service';
import { AuditHubService } from '../../services/audit-hub.service';
import { ToastService } from '../../services/toast.service';
import { BulkFixBatchDetail, BulkFixItem } from '../../interfaces/bulk-fix.interfaces';

@Component({
  selector: 'app-payment-plan-audit-bulk-fix',
  templateUrl: './payment-plan-audit-bulk-fix.component.html',
  styleUrls: ['./payment-plan-audit-bulk-fix.component.scss']
})
export class PaymentPlanAuditBulkFixComponent implements OnInit, OnDestroy {
  batchId!: string;
  batch: BulkFixBatchDetail | null = null;
  loading = false;
  itemFilter: 'all' | 'failed' | 'pending' = 'all';
  private subs: Subscription[] = [];
  private pollingHandle?: ReturnType<typeof setInterval>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auditService: PaymentPlanAuditService,
    private hubService: AuditHubService,
    private toast: ToastService
  ) {}

  async ngOnInit(): Promise<void> {
    this.batchId = this.route.snapshot.paramMap.get('batchId') ?? '';
    if (!this.batchId) {
      this.router.navigate(['/admin/payment-plan-audit']);
      return;
    }

    this.loading = true;
    try {
      this.batch = await firstValueFrom(this.auditService.getBulkFix(this.batchId));
    } catch {
      this.toast.showToast('error', 'No se pudo cargar el batch.');
      this.router.navigate(['/admin/payment-plan-audit']);
      return;
    } finally {
      this.loading = false;
    }

    try {
      await this.hubService.connect();
      this.wireSubs();
      if (this.batch.status === 'Running') {
        await this.hubService.subscribeToBulkFix(this.batchId);
        this.startPolling();
      }
    } catch {
      // El hub no es crítico — polling sigue.
      if (this.batch.status === 'Running') this.startPolling();
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    if (this.pollingHandle) clearInterval(this.pollingHandle);
    if (this.batchId) this.hubService.unsubscribeFromBulkFix(this.batchId).catch(() => {});
  }

  get isRunning(): boolean { return this.batch?.status === 'Running'; }

  get filteredItems(): BulkFixItem[] {
    if (!this.batch) return [];
    if (this.itemFilter === 'failed')  return this.batch.items.filter(i => i.status === 'Failed');
    if (this.itemFilter === 'pending') return this.batch.items.filter(i => i.status === 'Pending' || i.status === 'Running');
    return this.batch.items;
  }

  private wireSubs(): void {
    this.subs.push(
      this.hubService.bulkFixProgress$.subscribe(e => this.applyProgress(e)),
      this.hubService.bulkFixFinished$.subscribe(e => { void this.onFinished(e); }),
      this.hubService.bulkFixCancelled$.subscribe(e => this.onCancelled(e))
    );
  }

  private applyProgress(e: { batchId: string; itemId: number; status: string; affectedRows?: number; error?: string }): void {
    if (!this.batch || this.batch.id !== e.batchId) return;
    const item = this.batch.items.find(i => i.id === e.itemId);
    if (!item) return;
    item.status = e.status as BulkFixItem['status'];
    if (e.affectedRows != null) item.affectedRows = e.affectedRows;
    if (e.error) item.error = e.error;
  }

  private async onFinished(e: { batchId: string; status: string; successCount: number; failedCount: number; skippedCount: number }): Promise<void> {
    if (!this.batch || this.batch.id !== e.batchId) return;
    try {
      this.batch = await firstValueFrom(this.auditService.getBulkFix(e.batchId));
    } catch { /* swallow */ }
    if (this.pollingHandle) { clearInterval(this.pollingHandle); this.pollingHandle = undefined; }
    const verb = e.status === 'Cancelled' ? 'cancelado'
      : e.status === 'Failed' ? 'terminado con errores'
      : 'completado';
    this.toast.showToast('success', `Bulk fix ${verb}.`);
  }

  private onCancelled(e: { batchId: string }): void {
    if (!this.batch || this.batch.id !== e.batchId) return;
    this.batch = { ...this.batch, status: 'Cancelled' };
    if (this.pollingHandle) { clearInterval(this.pollingHandle); this.pollingHandle = undefined; }
  }

  private startPolling(): void {
    if (this.pollingHandle) return;
    this.pollingHandle = setInterval(async () => {
      if (!this.batch) return;
      try {
        this.batch = await firstValueFrom(this.auditService.getBulkFix(this.batch.id));
        if (this.batch.status !== 'Running' && this.pollingHandle) {
          clearInterval(this.pollingHandle);
          this.pollingHandle = undefined;
        }
      } catch { /* swallow */ }
    }, 2000);
  }

  async cancel(): Promise<void> {
    if (!this.batch) return;
    try {
      await firstValueFrom(this.auditService.cancelBulkFix(this.batch.id));
      this.toast.showToast('info', 'Cancelación solicitada.');
    } catch (err: any) {
      this.toast.showToast('error', err?.error?.error ?? 'No se pudo cancelar.');
    }
  }

  backToAudit(): void {
    this.router.navigate(['/admin/payment-plan-audit']);
  }

  formatTarget(i: BulkFixItem): string {
    if (i.targetEnrollmentId != null)         return `enrollment #${i.targetEnrollmentId}`;
    if (i.targetCorporateEnrollmentId != null) {
      const pid = i.targetParticipantId != null ? ` · part #${i.targetParticipantId}` : '';
      return `corp #${i.targetCorporateEnrollmentId}${pid}`;
    }
    if (i.targetVersionId != null)          return `version #${i.targetVersionId}`;
    if (i.targetCorporateVersionId != null) return `corp version #${i.targetCorporateVersionId}`;
    return '—';
  }

  downloadCsv(): void {
    if (!this.batch) return;
    const headers = ['ItemId', 'Target', 'OriginalFindings', 'Status', 'AffectedRows', 'NewVersionId', 'DurationMs', 'Error'];
    const rows = this.batch.items.map(i => {
      const target = this.formatTarget(i);
      const err = (i.error ?? '').replace(/"/g, '""').replace(/\n/g, ' ');
      return `${i.id},"${target}",${i.originalFindingsCount},${i.status},${i.affectedRows ?? ''},${i.newVersionId ?? ''},${i.durationMs ?? ''},"${err}"`;
    });
    const csv = headers.join(',') + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-fix-${this.batch.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
